<template>
  <div v-if="!loading">
    <!-- Clickable Badge Wrapper -->
    <div
      id="btn-token-badge-wrapper"
      ref="badgeRef"
      class="cursor-pointer bg-gray-800 border rounded-lg px-3 py-1.5 transition-all duration-200 hover:shadow-lg focus:outline-none focus:ring-1 focus:ring-cyan-600"
      :class="[
        tokenInfo.hasExceeded ? 'border-red-500' : 'border-gray-600 hover:border-cyan-600',
        'active:scale-[0.98]'
      ]"
      role="button"
      tabindex="0"
      :aria-label="store.managed ? $t('tokens.clickForDetailsManaged') : $t('tokens.clickForDetails')"
      @click="togglePopover"
      @keydown.enter="togglePopover"
      @keydown.space.prevent="togglePopover"
    >
      <div class="flex items-center gap-2">
        <span class="text-xs text-gray-400">{{ store.managed ? $t('tokens.titleManaged') : $t('tokens.title') }}</span>

        <!-- Unlimited -->
        <template v-if="tokenInfo.isUnlimited">
          <span class="material-icons !text-[14px] text-purple-400">all_inclusive</span>
        </template>

        <!-- Managed client: carica residua stile batteria (100 → 0), no credito -->
        <template v-else-if="store.managed">
          <span
            class="text-sm font-semibold"
            :class="tokenInfo.hasExceeded ? 'text-red-400' : 'text-white'"
          >
            {{ $t('tokens.remainingPercent', { pct: managedRemainingPct }) }}
          </span>
          <span class="inline-block w-16 h-1.5 rounded-full bg-gray-700 overflow-hidden align-middle" aria-hidden="true">
            <span
              class="block h-full rounded-full transition-[width] duration-300 motion-reduce:transition-none"
              :style="{ width: managedRemainingPct + '%', backgroundColor: meterColor }"
            ></span>
          </span>
        </template>

        <!-- Normal / Exceeded -->
        <template v-else>
          <span
            class="text-sm font-semibold"
            :class="tokenInfo.hasExceeded ? 'text-red-400' : 'text-white'"
          >
            {{ store.euroResiduo }}
          </span>
        </template>

        <!-- Refresh button inline -->
        <Button
          v-if="!tokenInfo.isUnlimited"
          id="btn-refresh-tokens"
          text
          rounded
          :loading="isRefreshing"
          :disabled="isRefreshing"
          class="text-gray-400 hover:text-cyan-600 hover:bg-gray-700 !w-5 !h-5 !min-w-0 !min-h-0 !p-0"
          :aria-label="store.managed ? $t('tokens.refreshTooltipManaged') : $t('tokens.refreshTooltip')"
          @click.stop="handleRefreshClick"
        >
          <template #icon>
            <span class="material-icons select-none" style="font-size: 16px" aria-hidden="true">refresh</span>
          </template>
        </Button>
      </div>
    </div>

    <!-- Popover with Detailed Breakdown -->
    <Popover ref="popoverRef" :pt="{ root: { class: 'w-80' } }">
      <div class="flex flex-col gap-4">
        <!-- Token Breakdown Section -->
        <div>
          <h3 class="text-base font-semibold text-white mb-3">
            {{ $t('tokens.breakdownTitle') }}
          </h3>
          <div class="flex flex-col gap-2">
            <!-- Unlimited Case -->
            <div v-if="tokenInfo.isUnlimited" class="text-sm text-[var(--p-surface-300)]">
              {{ $t('tokens.unlimitedTooltip') }}
            </div>
            <!-- Standard Case -->
            <template v-else>
              <div class="flex justify-between items-center">
                <span class="text-sm text-[var(--p-surface-300)]">{{ $t('tokens.usedLabel') }}:</span>
                <span class="text-sm font-medium text-white">{{ formatNumber(tokenInfo.tokenUsed) }} tokens</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-sm text-[var(--p-surface-300)]">{{ $t('tokens.allowanceLabel') }}:</span>
                <span class="text-sm font-medium text-white">{{ formatNumber(tokenInfo.tokenAllowance) }} tokens</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-sm text-[var(--p-surface-300)]">{{ $t('tokens.remainingLabel') }}:</span>
                <span
                  class="text-sm font-medium"
                  :class="tokenInfo.hasExceeded ? 'text-[var(--p-red-400)]' : 'text-[var(--p-cyan-400)]'"
                >
                  {{ formatNumber(tokenInfo.tokenRemaining) }} tokens
                </span>
              </div>
            </template>
          </div>
        </div>

        <!-- Estimated Capacity Section (only when NOT unlimited) -->
        <div v-if="!tokenInfo.isUnlimited" class="border-t border-[var(--p-surface-600)] pt-4">
          <h3 class="text-base font-semibold text-white mb-3">
            {{ $t('tokens.estimatedCapacityTitle') }}
          </h3>
          <div class="flex flex-col gap-2">
            <!-- Messages Estimate -->
            <div v-if="estimatedRemainingMessages >= 0" class="flex items-center gap-2">
              <span class="text-xl">💬</span>
              <span class="text-sm text-[var(--p-surface-300)]">
                {{ $t('tokens.estimatedMessages', { count: formatNumber(estimatedRemainingMessages) }) }}
              </span>
            </div>
            <!-- Voicebot Minutes Estimate -->
            <div v-if="estimatedRemainingMinutes >= 0" class="flex items-center gap-2">
              <span class="text-xl">🎙️</span>
              <span class="text-sm text-[var(--p-surface-300)]">
                {{ $t('tokens.estimatedMinutes', { count: formatNumber(estimatedRemainingMinutes) }) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Popover>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import api from '../../services/api';
import { useTokenUpdateEvent } from '../../composables/useTokenUpdateEvent';
import { useTokenEvents } from '../../composables/useWebSocket';
import { useTokenStore } from '../../composables/useTokenStore';
import { useWorkspaceStore } from '../../stores/workspaceStore';

const { updateTokens, store } = useTokenStore();
const workspaceStore = useWorkspaceStore();
const wsId = computed(() => workspaceStore.currentWorkspaceId);

/**
 * TokenBadge Component
 * 
 * Displays token usage information with real-time updates via WebSocket and polling.
 * Shows used/total tokens, progress bar (color-coded by usage %), and detailed breakdown.
 * 
 * Features:
 * - Clickable badge that opens Popover with detailed breakdown
 * - ProgressBar (cyan < 75%, orange 75-89%, red ≥ 90%)
 * - Manual refresh button with loading state
 * - Automatic polling every 30 seconds
 * - Real-time WebSocket updates
 * - Token usage estimates (messages + voicebot minutes)
 * - Special handling for unlimited tokens
 * - Keyboard accessible (Enter/Space to open Popover)
 * 
 * @component
 * @example
 * <TokenBadge ref="tokenBadgeRef" />
 * 
 * // Parent can trigger refresh:
 * tokenBadgeRef.value.refresh();
 */

const { t } = useI18n();
const { onTokenUpdate } = useTokenUpdateEvent();

// Refs
const badgeRef = ref(null);
const popoverRef = ref(null);
const loading = ref(true);
const isRefreshing = ref(false);
const pollTimer = ref(null);

// Constants
const POLLING_INTERVAL_MS = 30000; // 30 seconds
const MAX_POLLING_BACKOFF_MS = 5 * 60 * 1000; // cap backoff at 5 minutes

// Consecutive poll failures — drives exponential backoff and once-per-streak logging
let consecutiveFailures = 0;

// State
const tokenInfo = ref({
  tokenUsed: 0,
  tokenAllowance: 0,
  tokenRemaining: 0,
  isUnlimited: false,
  isNearLimit: false,
  hasExceeded: false
});

// Token estimation config
const tokenEstimationConfig = ref({
  tokensPerMessage: 100,
  tokensPerVoicebotMinute: 300
});

/**
 * Fetches token estimation configuration from public settings
 */
const fetchTokenEstimationConfig = async () => {
  try {
    const response = await api.get('/public/token-estimation');
    const data = response.data.data;
    if (data) {
      tokenEstimationConfig.value = {
        tokensPerMessage: data.tokensPerMessage || 100,
        tokensPerVoicebotMinute: data.tokensPerVoicebotMinute || 300
      };
      Console.log('[TokenBadge] Loaded token estimation config:', tokenEstimationConfig.value);
    }
  } catch (err) {
    Console.error('[TokenBadge] Error fetching token estimation config:', err);
    // Use defaults if API call fails
  }
};

/**
 * Calculates estimated remaining messages based on remaining tokens
 */
const estimatedRemainingMessages = computed(() => {
  if (tokenInfo.value.isUnlimited) return -1;
  const remaining = tokenInfo.value.tokenRemaining || 0;
  const tokensPerMsg = tokenEstimationConfig.value.tokensPerMessage || 100;
  return Math.floor(remaining / tokensPerMsg);
});

/**
 * Calculates estimated remaining minutes of voicebot conversation based on remaining tokens
 */
const estimatedRemainingMinutes = computed(() => {
  if (tokenInfo.value.isUnlimited) return -1;
  const remaining = tokenInfo.value.tokenRemaining || 0;
  const tokensPerMin = tokenEstimationConfig.value.tokensPerVoicebotMinute || 300;
  return Math.floor(remaining / tokensPerMin);
});

/**
 * Progress percentage for display (0-100)
 */
const progressPercentage = computed(() => {
  if (tokenInfo.value.tokenAllowance === 0) return 0;
  const used = tokenInfo.value.tokenUsed;
  const total = tokenInfo.value.tokenAllowance;
  return Math.min(100, (used / total) * 100);
});

/**
 * Managed client: percentage to display — uses the store's single source of
 * truth (kept live by both REST and WebSocket paths) with a local fallback.
 */
const managedPct = computed(() => store.consumptionPercentage ?? Math.round(progressPercentage.value));

/**
 * Managed client: carica residua in stile "batteria" (100 → 0).
 * Complemento del consumato, così testo e barra mostrano quanto resta e si
 * svuotano man mano. Il colore (meterColor) resta funzione del consumo, quindi
 * vira al rosso proprio quando la carica residua è bassa.
 */
const managedRemainingPct = computed(() => Math.max(0, 100 - managedPct.value));

/**
 * ProgressBar color based on usage percentage
 * Cyan (< 75%), Orange (75-89%), Red (≥ 90%)
 */
const meterColor = computed(() => {
  const pct = progressPercentage.value;
  if (pct >= 90) return '#ef4444';  // red-500
  if (pct >= 75) return '#f97316';  // orange-500
  return '#06b6d4';  // cyan-500
});

/**
 * Fetches token information from the API
 * Used by initial load, polling, and manual refresh
 */
const fetchTokenInfo = async () => {
  if (!wsId.value) {
    // Workspace not yet selected — keep current state, finish loading
    loading.value = false;
    return false;
  }
  try {
    const response = await api.get(`/workspaces/${wsId.value}/billing/tokens`);
    const data = response.data.data;
    // Guard against an empty/unexpected body (observed during backend deploy
    // windows): assigning undefined here made tokenInfo.value undefined and the
    // template then threw on tokenInfo.hasExceeded. Keep the last-good state instead.
    if (data) {
      tokenInfo.value = data;
      updateTokens(data);
    } else {
      Console.warn('[TokenBadge] Token response had no data payload; keeping previous token state');
    }
    consecutiveFailures = 0;
    Console.log('[TokenBadge] Loaded token info from API:', data);
    return true;
  } catch (err) {
    // Log only the first failure of a streak so a long outage (or a backend 504
    // storm) doesn't flood the console with one error per poll.
    if (consecutiveFailures === 0) {
      Console.error('[TokenBadge] Error fetching token info:', err);
    }
    consecutiveFailures++;
    // In case of error, show unlimited to avoid blocking UI
    tokenInfo.value.isUnlimited = true;
    return false;
  } finally {
    loading.value = false;
  }
};

/**
 * Handler for manual refresh button
 * Shows loading state during request
 */
const handleRefreshClick = async () => {
  isRefreshing.value = true;
  await fetchTokenInfo();
  isRefreshing.value = false;
};

/**
 * Toggles the Popover visibility
 */
const togglePopover = (event) => {
  if (popoverRef.value) {
    popoverRef.value.toggle(event);
  }
};

// runPoll and scheduleNextPoll are mutually recursive (each reschedules the
// other), so they are declared as hoisted function declarations to avoid any
// temporal-dead-zone ordering fragility.

/**
 * One polling tick. Skips the request entirely while the tab is hidden or the
 * browser is offline (the main source of the idle console-error floods), and
 * backs off exponentially while the connection/backend keeps failing.
 */
async function runPoll() {
  if (document.hidden || !navigator.onLine) {
    scheduleNextPoll(POLLING_INTERVAL_MS);
    return;
  }
  const ok = await fetchTokenInfo();
  const delay = ok
    ? POLLING_INTERVAL_MS
    : Math.min(POLLING_INTERVAL_MS * 2 ** Math.min(consecutiveFailures - 1, 4), MAX_POLLING_BACKOFF_MS);
  scheduleNextPoll(delay);
}

/**
 * Schedules the next poll after `delay` ms — a self-rescheduling setTimeout
 * (instead of a fixed setInterval) so the cadence can back off while failing.
 */
function scheduleNextPoll(delay = POLLING_INTERVAL_MS) {
  clearTimeout(pollTimer.value);
  pollTimer.value = setTimeout(runPoll, delay);
}

/**
 * Starts polling to sync tokens from production widgets
 */
const startPolling = () => {
  // Avoid duplicate polling if already active
  if (pollTimer.value) {
    Console.warn('[TokenBadge] Polling already active, skipping startPolling()');
    return;
  }
  Console.log('[TokenBadge] Starting polling every', POLLING_INTERVAL_MS / 1000, 'seconds');
  scheduleNextPoll(POLLING_INTERVAL_MS);
};

/**
 * Stops polling and clears the timer
 * Important to avoid memory leaks when component unmounts
 */
const stopPolling = () => {
  if (pollTimer.value) {
    Console.log('[TokenBadge] Stopping polling');
    clearTimeout(pollTimer.value);
    pollTimer.value = null;
  }
};

/**
 * Resets backoff and polls promptly when the user returns to the tab or the
 * network comes back, instead of waiting out a (possibly long) backoff delay.
 */
const handleResume = () => {
  if (!document.hidden && navigator.onLine) {
    consecutiveFailures = 0;
    scheduleNextPoll(0);
  }
};

/**
 * Handler for real-time token updates via WebSocket
 */
const handleWebSocketTokenUpdate = (data) => {
  Console.group('[TokenBadge] WebSocket Token Update');
  Console.log('Received data:', data);
  // Update local ref for consistency
  tokenInfo.value.tokenUsed = data.tokenUsed;
  tokenInfo.value.tokenAllowance = data.tokenAllowance;
  tokenInfo.value.tokenRemaining = data.tokenRemaining;
  
  // Recalculate states
  tokenInfo.value.isUnlimited = data.tokenAllowance === -1;
  tokenInfo.value.hasExceeded = !tokenInfo.value.isUnlimited && data.tokenRemaining <= 0;
  tokenInfo.value.isNearLimit = !tokenInfo.value.isUnlimited && 
                                !tokenInfo.value.hasExceeded && 
                                (data.tokenUsed / data.tokenAllowance) >= 0.8;
  updateTokens(tokenInfo.value);
  Console.log('Updated tokenInfo:', tokenInfo.value);
  Console.groupEnd();
};

/**
 * Handler for token updates from preview (real-time)
 * Receives updates from widget preview on platform
 */
const handlePreviewTokenUpdate = (data) => {
  Console.group('[TokenBadge] Preview Token Update');
  Console.log('Data received:', data);
  Console.log('Current tokenInfo before update:', tokenInfo.value);
  
  // Ignore updates with zero values (happens in preview mode without authenticated user)
  if (data && data.tokenUsed === 0 && data.tokenAllowance === 0) {
    Console.log('Ignoring preview update with zero tokens (no authenticated user)');
    Console.groupEnd();
    return;
  }
  
  handleWebSocketTokenUpdate(data);
  Console.log('Current tokenInfo after update:', tokenInfo.value);
  Console.groupEnd();
};

/**
 * Format number with thousand separators
 */
const formatNumber = (num) => {
  return new Intl.NumberFormat().format(num);
};

// Expose refresh method to parent components
defineExpose({
  refresh: fetchTokenInfo
});

onMounted(() => {
  Console.log('[TokenBadge] Mounted');
  fetchTokenInfo();
  fetchTokenEstimationConfig();
  
  // Register listener for token updates from preview (widget preview on platform)
  const unsubscribe = onTokenUpdate(handlePreviewTokenUpdate);
  
  // Subscribe to WebSocket events for real-time updates
  useTokenEvents(handleWebSocketTokenUpdate);
  
  // Start polling to sync consumption from production widgets
  // Polling is silent (no UI feedback) and serves as "safety net" for widgets
  // deployed on user websites that cannot send events
  startPolling();

  // Poll promptly again when the tab regains focus or the network returns
  window.addEventListener('online', handleResume);
  document.addEventListener('visibilitychange', handleResume);

  // Cleanup on unmount
  onUnmounted(() => {
    Console.log('[TokenBadge] Unmounting');
    unsubscribe();
    stopPolling();
    window.removeEventListener('online', handleResume);
    document.removeEventListener('visibilitychange', handleResume);
  });
});

watch(wsId, (newId, oldId) => {
  if (newId && newId !== oldId) {
    fetchTokenInfo();
  }
});
</script>
